import React from 'react';

/**
 * S11.2 — make a non-button element behave like one for a keyboard.
 *
 * Several screens use a `<div onClick>` for rows and cards where a real
 * `<button>` would fight the layout (a grid card containing its own links, a
 * flex row of columns). A click handler on a `div` is invisible to tab order
 * and does nothing on Enter, so the action does not exist for anyone not using
 * a mouse — it is not a degraded experience, it is an absent one.
 *
 * This returns the four things such an element needs, together, so none of them
 * is forgotten individually: a role, a tab stop, the click, and Enter/Space.
 * Space is included because that is what a button does, and `preventDefault`
 * stops it scrolling the page instead.
 */
export function activatable(
  onActivate: () => void,
  label?: string,
): {
  role: 'button';
  tabIndex: 0;
  'aria-label'?: string;
  onClick: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
} {
  return {
    role: 'button',
    tabIndex: 0,
    ...(label ? { 'aria-label': label } : {}),
    onClick: onActivate,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
      }
    },
  };
}
